import React, { useState, useEffect, Suspense } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { getApiErrorMessage } from '../shared/utils/apiError'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@jira-clone/ui'
import { Target, Plus, CheckCircle2, TrendingUp, ArrowLeft } from 'lucide-react'
import { cn, cardClassName } from '@jira-clone/ui'

interface Goal {
  id: string
  title: string
  description?: string
  progress: number
  status: string
  organizationId: string
}

function GoalsContent() {
  const navigate = useNavigate()
  const { organization } = useAuth()
  const queryClient = useQueryClient()

  const { data: goals = [], isLoading, refetch: refetchGoals } = useQuery({
    queryKey: ['goals', organization?.id],
    queryFn: async () => {
      const res = await api.get<any>('/goals', { organizationId: organization?.id })
      return (res?.data || []) as Goal[]
    },
    enabled: !!organization?.id,
  })

  // Create Goal modal state
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [creating, setCreating] = useState(false)

  // Listen for goalCreated events
  useEffect(() => {
    const handleUpdate = () => refetchGoals()
    window.addEventListener('goalCreated', handleUpdate)
    return () => window.removeEventListener('goalCreated', handleUpdate)
  }, [refetchGoals])

  const createGoalMutation = useMutation({
    mutationFn: (data: { title: string; description?: string; organizationId: string }) => {
      const now = new Date()
      const endDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000)
      return api.post(`/goals?organizationId=${data.organizationId}`, {
        title: data.title,
        description: data.description,
        startDate: now.toISOString(),
        endDate: endDate.toISOString(),
      }, { skipErrorToast: true })
    },
    onSuccess: () => {
      setTitle('')
      setDescription('')
      setIsOpen(false)
      queryClient.invalidateQueries({ queryKey: ['goals', organization?.id] })
      window.dispatchEvent(new CustomEvent('goalCreated'))
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'create goals'))
    },
    onSettled: () => setCreating(false),
  })

  const updateGoalProgressMutation = useMutation({
    mutationFn: ({ id, progress }: { id: string; progress: number }) =>
      api.patch(`/goals/${id}/progress`, { progress }, { skipErrorToast: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', organization?.id] })
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'update goal progress'))
    },
  })

  const handleCreateGoal = (e: React.FormEvent) => {
    e.preventDefault()
    if (!title || !organization?.id) return
    setCreating(true)
    createGoalMutation.mutate({
      title,
      description,
      organizationId: organization.id,
    })
  }

  const handleUpdateProgress = (id: string, newProgress: number) => {
    updateGoalProgressMutation.mutate({ id, progress: newProgress })
  }

  return (
    <div className="space-y-6 w-full max-w-[1800px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#6825F5] to-[#7C3AED] text-white shadow-sm shadow-purple-200 transition-all hover:shadow-md hover:shadow-purple-300/50"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-foreground">Goals & OKRs</h2>
            <p className="text-sm text-muted-foreground">Strategic milestones and key business results</p>
          </div>
        </div>
        <Button onClick={() => setIsOpen(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          <span>New Goal</span>
        </Button>
      </div>

      {/* Goals Grid */}
      <div className="space-y-4">
        {goals.map((goal) => (
          <Card key={goal.id} className="transition-colors" interactive>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary shrink-0" />
                    <h3 className="font-bold text-base text-foreground truncate">{goal.title}</h3>
                    <Badge variant={goal.status === 'completed' ? 'success' : 'outline'} className="text-[10px] uppercase">
                      {goal.status}
                    </Badge>
                  </div>
                  {goal.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 pl-6">{goal.description}</p>
                  )}
                </div>

                {/* Progress Control */}
                <div className="flex items-center gap-4 shrink-0 w-full md:w-72">
                  <div className="flex-1 space-y-1.5">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="text-foreground font-mono">{goal.progress}%</span>
                    </div>
                    {/* Progress Bar */}
                    <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-300 rounded-full"
                        style={{ width: `${Math.min(goal.progress, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Increment buttons */}
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs px-2"
                      onClick={() => handleUpdateProgress(goal.id, Math.max(0, goal.progress - 10))}
                    >
                      -10%
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs px-2"
                      onClick={() => handleUpdateProgress(goal.id, Math.min(100, goal.progress + 10))}
                    >
                      +10%
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {goals.length === 0 && !isLoading && (
          <div className={cn(cardClassName(), 'text-center py-16')}>
            <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-40" />
            <h3 className="font-semibold text-foreground">No goals defined</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Set clear objectives to keep your organization aligned.
            </p>
            <Button onClick={() => setIsOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-1" /> Create Goal
            </Button>
          </div>
        )}
      </div>

      {/* Create Goal Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Strategic Goal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGoal} className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label htmlFor="goalTitle">Objective / Goal Title</Label>
              <Input
                id="goalTitle"
                placeholder="Increase ARR to $5M in Q4"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="goalDesc">Description & Key Results</Label>
              <Textarea
                id="goalDesc"
                placeholder="Key milestones and metrics for this objective..."
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={creating}>
                Save Goal
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default function GoalsPage() {
  return (
    <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Loading goals...</div>}>
      <GoalsContent />
    </Suspense>
  )
}